import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import CardSubtitle from "reactstrap/lib/CardSubtitle";
import {
  Card,
  Row,
  Col,
  CardTitle,
  Button,
  Badge,
  Input,
  Form,
  Label,
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  HandleUserNameChange,
  HandleUserEmailChange,
  CheckIfEmailIsOccupied,
  HandleClassEdit,
  HandleDeleteStudent,
  SaveClassFocusControlSetting,
  DeleteClassFocusControlSetting,
} from "../../actions/teacherMutations";
import ConfirmDialog from "../Modal/ConfirmDialog";
import { toast } from "react-toastify";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import { FormattedMessage, useIntl } from "react-intl";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import DataGrid from "../DataGrids/DataGrid";
import DisconnectTeacherFromClassButton from "../Button/DisconnectTeacherFromClassButton";
import AddNewTeacherToClass from "../Modal/AddNewTeacherToClass";
import AddTeachersToClass from "../Modal/AddTeachersToClass";
import StudentLicenseManagemenModal from "../Modal/StudentLicenseManagemenModal";
import FinalExamUnlockerContainer from "./FinalExamUnlockerContainer";
import CreateStudentInClass from "../Modal/CreateStudentInClass";
import MoveExistingStudentToClassModal from "../Modal/MoveExistingStudentToClassModal";
import ImportStudentsToClassModal from "../Modal/ImportStudentsToClassModal";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import ModuleLocker from "./ModuleLocker";
import { GetCoursesByClassId } from "../../actions/teacherQueries";
import { formatDate } from "../../Utilities/dateUtils";
import { toStudentLicenseName } from "../../Utilities/licenseHelper";
import { compareByName, compareStrings } from "../../Utilities/sortHelper";
import FocusControl from "../Teacher/FocusControl";
import useFocusControl from "./hooks/useFocusControl";
import useAddNewTeacherToClass from "./hooks/useAddNewTeacherToClass";
import useCartStore from "../../stores/useCartStore";
import StudentLicenseStatusIcon from "./StudentLicenseStatusIcon";
import RequiresFullAccessHint from "./RequiresFullAccessHint";

export default function ClassAdministration(props) {
  const intl = useIntl();

  const classCoursesData = GetCoursesByClassId(props.classData.data.id);

  const [teacherForRemoval, setTeacherForRemoval] = useState("");
  const [removingTeacher, setRemovingTeacher] = useState(false);
  const [promptTeacherRemoval, setPromptTeacherRemoval] = useState(false);
  const [studentForRemoval, setStudentForRemoval] = useState("");
  const [promptStudentRemoval, setPromptStudentRemoval] = useState(false);
  const [addNewStudentToClassModal, setAddNewStudentToClassModal] =
    useState(false);
  const [addStudentToClassModal, setAddStudentToClassModal] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [studentsForLicenseModal, setStudentsForLicenseModal] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [addNewTeacherModalOpen, setAddNewTeacherModalOpen] = useState(false);
  const [addExistingTeachersModalOpen, setAddExistingTeachersModalOpen] =
    useState(false);
  const [updatingClass, setUpdatingClass] = useState(false);
  const [showCourses, setShowCourses] = useState(false);
  const [importStudentsModalOpen, setImportStudentsModalOpen] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [classNameInvalidSigns, setClassNameInvalidSigns] = useState(false);
  const [studentFilterSearch, setStudentFilterSearch] = useState("");
  const [teacherFilterSearch, setTeacherFilterSearch] = useState("");

  const mutateStudentName = useMutation({ mutationFn: HandleUserNameChange });
  const mutateStudentEmail = useMutation({ mutationFn: HandleUserEmailChange });
  const addNewTeacherToClass = useAddNewTeacherToClass();
  const mutateCheckStudentEmail = useMutation({
    mutationFn: CheckIfEmailIsOccupied,
  });
  const mutateDeleteStudent = useMutation({ mutationFn: HandleDeleteStudent });
  const mutateEditClass = useMutation({ mutationFn: HandleClassEdit });
  const queryClient = useQueryClient();
  const clearCart = useCartStore((s) => s.clearCart);

  const buildDeviantData = useCallback(
    (fcSettings) => {
      const moduleItem = fcSettings.find((x) => x.classSetting?.target === 0);
      const finalItem = fcSettings.find((x) => x.classSetting?.target === 1);
      const allDeviantStudents = [
        ...(moduleItem?.deviantStudentSettings || []),
        ...(finalItem?.deviantStudentSettings || []),
      ];
      const uniqueDeviantStudentIds = [
        ...new Set(allDeviantStudents.map((s) => s.studentId)),
      ];
      const students = props.classData.data.students || [];
      const deviantStudents = uniqueDeviantStudentIds.map((studentId) => {
        const student = students.find((s) => s.id === studentId);
        return {
          studentId,
          studentName: student?.name || null,
          className: props.classData.data.name,
        };
      });
      return { deviantClasses: [], deviantStudents };
    },
    [props.classData.data.students, props.classData.data.name],
  );

  const onRefreshSettings = useCallback(async () => {
    const classId = String(props.classData.data.id);
    await queryClient.refetchQueries({
      queryKey: ["FetchClassByClassId", classId],
      exact: true,
    });
    const updatedClass = queryClient.getQueryData([
      "FetchClassByClassId",
      classId,
    ]);
    return updatedClass?.focusControlSettings || [];
  }, [queryClient, props.classData.data.id]);

  const fc = useFocusControl({
    settingKey: "classSetting",
    entityId: props.classData.data.id,
    entityIdKey: "classId",
    focusControlSettings: props.classData.data.focusControlSettings,
    saveFn: SaveClassFocusControlSetting,
    deleteFn: DeleteClassFocusControlSetting,
    onRefreshSettings,
    buildDeviantData,
  });

  if (classCoursesData.isPending || !classCoursesData.data) {
    return (
      <div className="inline-pacman-loader">
        <PacmanLoader color="#20a8d8" size={20} />
      </div>
    );
  }

  let values = {
    id: props.classData.data.id,
    name: props.classData.data.name,
    school: props.teacherInfo.data.customer.name,
    programme: props.classData.data.programme,
    orientation: props.classData.data.orientation,
    teachers: props.classData.data.teachers,
  };

  const promptRemoveTeacher = (teacher) => {
    setTeacherForRemoval(teacher);
    setPromptTeacherRemoval(true);
  };

  const promptRemoveStudent = (student) => {
    setStudentForRemoval(student);
    setPromptStudentRemoval(true);
  };

  const toggleImportStudentsModal = () => {
    setImportStudentsModalOpen((prev) => !prev);
  };

  const reloadClass = () => {
    queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
    queryClient.invalidateQueries({ queryKey: ["FetchClassByClassId"] });
  };

  var allStudents = [];

  props.teacherInfo.data.programmes.forEach((prog) => {
    prog.classes.forEach((cl) => {
      cl.students.forEach((stud) => {
        allStudents.push(stud);
      });
    });
  });

  allStudents = allStudents.sort(compareByName);
  var studentsInClass = [];
  props.classData.data.students.forEach((stud) => {
    studentsInClass.push(stud);
  });

  const openAddNewStudentToClassModal = () => {
    toggleModals("addNewStudentToClass");
  };
  const openAddStudentToClassModal = () => {
    toggleModals("addStudentToClass");
  };

  const toggleModals = (entity) => {
    switch (entity) {
      case "addNewStudentToClass":
        setAddNewStudentToClassModal(!addNewStudentToClassModal);
        break;
      case "addStudentToClass":
        setAddStudentToClassModal(!addStudentToClassModal);
        break;
      default:
        this.setState({ studentModal: !this.state.studentModal });
    }
  };

  const handleAddNewTeacher = (values) => {
    setAddNewTeacherModalOpen(false);
    addNewTeacherToClass(values, props.classData.data.id);
  };

  const toggleNewTeacherModal = () => {
    setAddNewTeacherModalOpen((prevstate) => !prevstate);
  };

  const toggleExistingTeachersModal = () => {
    setAddExistingTeachersModalOpen((prevstate) => !prevstate);
  };

  const handleRemoveTeacher = async () => {
    setRemovingTeacher(true);
    setPromptTeacherRemoval(false);
    let tempClassData = props.classData.data;
    var teachers = props.classData.data.teachers.filter(function (teacher) {
      return teacher.id !== teacherForRemoval.id;
    });
    tempClassData.teachers = teachers;
    queryClient.setQueryData(
      ["FetchClassByClassId", props.classData.data.id],
      tempClassData,
    );
    await mutateEditClass.mutate(
      {
        id: values.id,
        name: values.name,
        school: values.school,
        programme: values.programme,
        orientation: values.orientation,
        teachers: teachers,
      },
      {
        onSuccess: () => {
          setRemovingTeacher(false);
          queryClient.invalidateQueries({
            queryKey: ["FetchTeacherInformation"],
          });
          toast.success(<FormattedMessage id="ClassUpdated" />);
        },
        onError: (err) => {
          tempClassData.teachers.push(teacherForRemoval);
          queryClient.setQueryData(
            ["FetchClassByClassId", props.classData.data.id],
            tempClassData,
          );
          setRemovingTeacher(false);
          setPromptTeacherRemoval(false);
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      },
    );
  };

  const handleDeleteStudent = () => {
    setPromptStudentRemoval(false);
    setUpdatingClass(true);
    mutateDeleteStudent.mutate(
      {
        studentEmail: studentForRemoval.email,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["FetchTeacherInformation"],
          });
          queryClient.invalidateQueries({
            queryKey: ["FetchClassByClassId"],
          });
          setUpdatingClass(false);
          toast.success(<FormattedMessage id="StudentDeleted" />);
        },
        onError: (err) => {
          setUpdatingClass(false);
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      },
    );
  };

  const handleChangedClassName = (event) => {
    if (
      event.target.name === "className" &&
      event.target.value !== props.classData.data.name
    ) {
      if (!event.target.value.match(/^[A-Za-z0-9,\-,",Å,å,Ä,ä,Ö,ö ]+$/)) {
        setClassNameInvalidSigns(true);
        return null;
      }
      setClassNameInvalidSigns(false);
      setUpdatingClass(true);
      values.newName = event.target.value;
      mutateEditClass.mutate(
        {
          id: props.classData.data.id,
          name: values.newName,
          school: values.school,
          programme: values.programme,
          orientation: values.orientation,
          teachers: values.teachers,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["FetchTeacherInformation"],
            });
            queryClient.invalidateQueries({
              queryKey: ["FetchClassByClassId"],
            });
            setPromptTeacherRemoval(false);
            setUpdatingClass(false);
            toast.success(<FormattedMessage id="ClassUpdated" />);
          },
          onError: (err) => {
            setPromptTeacherRemoval(false);
            setUpdatingClass(false);
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        },
      );
    } else {
      setClassNameInvalidSigns(false);
    }
  };

  const handleChangeProgram = (event, field) => {
    if (
      (field === "classProgram" &&
        event.value !== props.classData.data.programme) ||
      (field === "classOrientation" &&
        event.value !== props.classData.data.orientation)
    ) {
      setUpdatingClass(true);
      if (field === "classProgram") {
        values.programme = event.value;
      } else if (field === "classOrientation") {
        values.orientation = event.value;
      }
      mutateEditClass.mutate(
        {
          id: props.classData.data.id,
          name: values.name,
          school: values.school,
          programme: values.programme,
          orientation: values.orientation,
          teachers: values.teachers,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["FetchTeacherInformation"],
            });
            queryClient.invalidateQueries({
              queryKey: ["FetchClassByClassId"],
            });
            setPromptTeacherRemoval(false);
            setUpdatingClass(false);
            toast.success(<FormattedMessage id="ClassUpdated" />);
          },
          onError: (err) => {
            setPromptTeacherRemoval(false);
            setUpdatingClass(false);
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        },
      );
    }
  };

  const handleChangedUser = (event, user, field, userType) => {
    if (field === "name") {
      handleChangedUserName(user, event.target.value, userType);
    } else if (field === "email") {
      checkEmailOnChange(user, event.target.value, userType);
    }
  };

  const handleChangedUserName = async (user, newUserName, userType) => {
    if (newUserName !== user.name) {
      const nameCheck = checkNameLengthAndCharacters(newUserName);
      if (nameCheck !== true) {
        toast.warn(nameCheck);
      } else {
        setUpdatingUserId(user.id);
        mutateStudentName.mutate(
          {
            oldEmail: user.email,
            newEmail: user.email,
            newName: newUserName,
          },
          {
            onSuccess: () => {
              let tempClassData = props.classData.data;
              if (userType === "student") {
                tempClassData.students.find(
                  (stud) => stud.id === user.id,
                ).name = newUserName;
              } else if (userType === "teacher") {
                tempClassData.teachers.find(
                  (teach) => teach.id === user.id,
                ).name = newUserName;
              }
              queryClient.refetchQueries({
                queryKey: ["FetchTeacherInformation"],
              });
              queryClient.setQueryData(
                ["FetchClassByClassId", props.classData.data.id],
                tempClassData,
              );
              setUpdatingUserId(null);
              toast.success(<FormattedMessage id="UserUpdated" />);
            },
            onError: (err) => {
              setUpdatingUserId(null);
              toast.warn(formatServiceError(intl, err?.response?.data));
            },
          },
        );
      }
    }
  };

  const checkEmailOnChange = (user, newUserEmail, userType) => {
    if (user.email !== newUserEmail) {
      if (
        !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
          newUserEmail,
        )
      ) {
        toast.warn(<FormattedMessage id="WrongEmailFormat" />);
      } else {
        setUpdatingUserId(user.id);
        mutateCheckStudentEmail.mutate(
          {
            oldEmail: user.email,
            newEmail: newUserEmail,
            studentName: user.name,
          },
          {
            onSuccess: (data) => {
              if (data.data === true) {
                setUpdatingUserId(null);
                toast.warn(<FormattedMessage id="EmailOccupied" />);
              } else {
                handleChangedUserEmail(user, newUserEmail, userType);
              }
            },
            onError: (err) => {
              setUpdatingUserId(null);
              toast.warn(formatServiceError(intl, err?.response?.data));
            },
          },
        );
      }
    }
  };

  const handleChangedUserEmail = (user, newUserEmail, userType) => {
    mutateStudentEmail.mutate(
      {
        oldEmail: user.email,
        newEmail: newUserEmail,
        userName: user.name,
      },
      {
        onSuccess: () => {
          let tempClassData = props.classData.data;
          if (userType === "student") {
            tempClassData.students.find((stud) => stud.id === user.id).email =
              newUserEmail;
          } else if (userType === "teacher") {
            tempClassData.teachers.find((teach) => teach.id === user.id).email =
              newUserEmail;
          }
          queryClient.setQueryData(
            ["FetchClassByClassId", props.classData.data.id],
            tempClassData,
          );
          setUpdatingUserId(null);
          toast.success(<FormattedMessage id="UserUpdated" />);
        },
        onError: (err) => {
          toast.warn(formatServiceError(intl, err?.response?.data));
          setUpdatingUserId(null);
        },
      },
    );
  };

  const compareUserField = (userProperty) => (a, b, columnId) =>
    compareStrings(
      a.original[userProperty][columnId],
      b.original[userProperty][columnId],
    );

  const compareFormattedPriority = (a, b, columnId) => {
    const valA = a.getValue(columnId);
    const valB = b.getValue(columnId);
    if (!valA) return -1;
    if (!valB) return 1;
    const priorityA = valA.props.id;
    const priorityB = valB.props.id;
    if (priorityA === priorityB) {
      return 0;
    }
    return priorityA < priorityB ? -1 : 1;
  };

  const renderLicenseNeededHeader = () => {
    const classNeedsLicense = props.classData.data.students.some(
      (stud) => stud.requiredLicenseIsMissing,
    );
    if (!classNeedsLicense) {
      return <FormattedMessage id="LicenseStatus" />;
    }
    const idsNeedingLicense = filteredStudents
      .filter((stud) => stud.requiredLicenseIsMissing)
      .map((stud) => stud.id);
    const allNeedingLicenseSelected =
      idsNeedingLicense.length > 0 &&
      idsNeedingLicense.every((id) => selectedStudentIds.includes(id));

    return (
      <div className="d-flex align-items-center">
        <Input
          type="checkbox"
          className="me-2 mt-0"
          checked={allNeedingLicenseSelected}
          disabled={
            !props.teacherInfo.data.purchaser || idsNeedingLicense.length === 0
          }
          onChange={() => toggleAllNeedingLicenseSelection(idsNeedingLicense)}
        />
        <Button
          color="primary"
          outline
          size="sm"
          disabled={
            !props.teacherInfo.data.purchaser || selectedStudentIds.length === 0
          }
          onClick={openLicenseModalForSelected}
        >
          <FormattedMessage id="AssignLicense" />
          {selectedStudentIds.length > 0
            ? ` (${selectedStudentIds.length})`
            : ""}
        </Button>
      </div>
    );
  };

  const studentTableColumns = [
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 160,
      sortingFn: compareUserField("studentData"),
    },
    {
      id: "email",
      key: "email",
      header: () => <FormattedMessage id="Email" />,
      width: 160,
      sortingFn: compareUserField("studentData"),
    },
    {
      id: "activatedAccount",
      key: "activatedAccount",
      header: () => <FormattedMessage id="Activated" />,
      width: 80,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "disconnect",
      key: "disconnect",
      header: () => <FormattedMessage id="Edit" />,
      width: 100,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "licences",
      key: "licences",
      header: () => <FormattedMessage id="ProgramLicense" />,
      width: 160,
      sortingFn: (a, b, columnId) =>
        a.getValue(columnId).length - b.getValue(columnId).length,
      cell: ({ row, getValue }) =>
        updatingUserId === row.original.uniqueId ? (
          ""
        ) : (
          <div className="d-flex align-items-center flex-wrap gap-1 program-license-cell">
            {getValue()}
            {row.original.studentData.class?.programme?.isAplProgram ? (
              ""
            ) : (
              <StudentLicenseStatusIcon
                student={row.original.studentData}
                programName={props.classData.data.programme?.name}
              />
            )}
          </div>
        ),
    },
    {
      id: "licenseNeeded",
      key: "licenseNeeded",
      width: 120,
      sortable: false,
      header: renderLicenseNeededHeader,
    },
    {
      id: "aplLicense",
      key: "aplLicense",
      width: 160,
      header: () => <FormattedMessage id="AplLicense" />,
      sortingFn: compareFormattedPriority,
    },
  ];

  const studentEditField = (student, field) => {
    return (
      <Input
        defaultValue={student[field]}
        onBlur={(event) => handleChangedUser(event, student, field, "student")}
      ></Input>
    );
  };
  const teacherEditField = (teacher, field) => {
    if (props.teacherInfo.data.fullAccess) {
      return (
        <Input
          defaultValue={teacher[field]}
          onBlur={(event) =>
            handleChangedUser(event, teacher, field, "teacher")
          }
        ></Input>
      );
    } else {
      return teacher[field];
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const toggleAllNeedingLicenseSelection = (idsNeedingLicense) => {
    setSelectedStudentIds((prev) => {
      const allSelected = idsNeedingLicense.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !idsNeedingLicense.includes(id));
      }
      return [...new Set([...prev, ...idsNeedingLicense])];
    });
  };

  const openLicenseModalForSelected = () => {
    const selectedStudents = props.classData.data.students.filter((stud) =>
      selectedStudentIds.includes(stud.id),
    );
    clearCart();
    setStudentsForLicenseModal(selectedStudents);
    setLicenseModalOpen(true);
  };

  const toggleStudentLicenseModal = () => {
    if (licenseModalOpen) {
      clearCart();
      setSelectedStudentIds([]);
      setStudentsForLicenseModal([]);
    }
    setLicenseModalOpen((prev) => !prev);
  };

  const licensesBadge = (stud) => {
    let studentHasLicense = false;
    stud.licenses.forEach((studLic) => {
      if (studLic.programLicenses.length > 0) {
        studentHasLicense = true;
      }
    });

    if (!studentHasLicense) {
      return "";
    }

    // Grön markering på licensen som hör till klassens program, så en elev med flera
    // licenser visar vilken som är den relevanta.
    const classCostCenter = props.classData.data.programme?.costCenter;

    let licenses = [];
    stud.licenses.forEach((studLic) => {
      studLic.programLicenses.forEach((license) => {
        licenses.push({
          name: toStudentLicenseName(license.name),
          isCurrent: license.fortKnoxCostCenter === classCostCenter,
        });
      });
    });
    return licenses.map((license, index) => (
      <Badge
        pill
        key={index}
        color={license.isCurrent ? "success" : "secondary"}
      >
        {license.name}
      </Badge>
    ));
  };
  const studentActivationHandler = (student) => {
    if (student.activatedAccount) {
      return <FormattedMessage id="Activated" />;
    } else {
      return (
        <ButtonSendActivationMail
          user={student}
          printButtonText={false}
          leftAlign={true}
        />
      );
    }
  };

  const filterBySearch = (data, search) => {
    if (!search) return data;
    const loweredSearch = search.toLowerCase();
    return data.filter(
      (s) =>
        s.name.toLowerCase().includes(loweredSearch) ||
        s.email.toLowerCase().includes(loweredSearch),
    );
  };

  const filteredStudents = filterBySearch(
    props.classData.data.students,
    studentFilterSearch,
  );

  const showAplLicenseStatus = (student) => {
    if (!student) return null;

    if (!student.hasValidAplLicense && !student.hasAplLicense) return null;

    return (
      <div
        className={
          student.hasValidAplLicense
            ? undefined
            : "text-muted text-decoration-line-through"
        }
        style={{ fontSize: "0.8rem" }}
      >
        {formatDate(student.aplLicenseValidFrom, "yyyy-MM-dd") +
          " - " +
          formatDate(student.aplLicenseValidTo, "yyyy-MM-dd")}
      </div>
    );
  };

  const studentTableDataSource = [];
  filteredStudents.sort(compareByName).forEach((stud) => {
    let student = {
      uniqueId: stud.id,
      name:
        updatingUserId === stud.id ? (
          <div style={{ height: "35px" }}>
            <PacmanLoader size={15} color={"#20a8d8"} loading={true} />
          </div>
        ) : (
          studentEditField(stud, "name")
        ),
      email: updatingUserId === stud.id ? "" : studentEditField(stud, "email"),
      activatedAccount:
        updatingUserId === stud.id ? "" : studentActivationHandler(stud),
      disconnect:
        updatingUserId === stud.id ? (
          ""
        ) : (
          <Button
            size="sm"
            outline
            color="primary"
            onClick={() => promptRemoveStudent(stud)}
          >
            <FormattedMessage id="DeleteStudent" />
          </Button>
        ),
      licences: updatingUserId === stud.id ? "" : licensesBadge(stud),
      // Ingen bock för den som redan har licensen — den gröna badgen säger det.
      licenseNeeded: stud.requiredLicenseIsMissing ? (
        <Input
          type="checkbox"
          className="mt-0 align-middle"
          checked={selectedStudentIds.includes(stud.id)}
          disabled={!props.teacherInfo.data.purchaser}
          onChange={() => toggleStudentSelection(stud.id)}
        />
      ) : (
        ""
      ),
      aplLicense: showAplLicenseStatus(stud),
      studentData: stud,
    };
    studentTableDataSource.push(student);
  });

  const teacherTableColumns = [
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 250,
      sortingFn: compareUserField("teacherData"),
    },
    {
      id: "email",
      key: "email",
      header: () => <FormattedMessage id="Email" />,
      width: 250,
      sortingFn: compareUserField("teacherData"),
    },
    {
      id: "activatedAccount",
      key: "activatedAccount",
      header: () => <FormattedMessage id="Activated" />,
      width: 150,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "purchaser",
      key: "purchaser",
      header: () => <FormattedMessage id="Purchaser" />,
      width: 150,
      sortingFn: compareFormattedPriority,
    },

    {
      id: "fullAccessTeacher",
      key: "fullAccessTeacher",
      header: () => <FormattedMessage id="FullTeacherAccess" />,
      width: 180,
      sortingFn: compareFormattedPriority,
    },
    {
      id: "edit",
      key: "edit",
      header: () => <FormattedMessage id="Edit" />,
      width: 180,
      sortingFn: compareFormattedPriority,
    },
  ];

  const filteredTeachers = filterBySearch(
    props.classData.data.teachers,
    teacherFilterSearch,
  );

  const teacherTableDataSource = [];
  filteredTeachers.sort(compareByName).forEach((teacher) => {
    teacherTableDataSource.push({
      uniqueId: teacher.id,
      name:
        updatingUserId === teacher.id ? (
          <div style={{ height: "35px" }}>
            <PacmanLoader size={15} color={"#20a8d8"} loading={true} />
          </div>
        ) : (
          teacherEditField(teacher, "name")
        ),
      email:
        updatingUserId === teacher.id ? "" : teacherEditField(teacher, "email"),
      activatedAccount:
        updatingUserId === teacher.id ? (
          ""
        ) : teacher.activatedAccount ? (
          <FormattedMessage id="Activated" />
        ) : (
          <FormattedMessage id="NotActivated" />
        ),
      purchaser:
        updatingUserId === teacher.id ? (
          ""
        ) : teacher.purchaser ? (
          <FormattedMessage id="IsPurchaser" />
        ) : (
          <FormattedMessage id="NotPurchaser" />
        ),
      fullAccessTeacher:
        updatingUserId === teacher.id ? (
          ""
        ) : teacher.fullAccess ? (
          <FormattedMessage id="true" />
        ) : (
          <FormattedMessage id="false" />
        ),
      edit:
        updatingUserId === teacher.id ? (
          ""
        ) : (
          <div className="d-flex flex-column gap-1">
            {props.fullAccessTeacher ? (
              <Button
                size="sm"
                color="primary"
                outline
                tag={Link}
                to={`teacherview/${teacher.id}/${encodeURIComponent(teacher.name)}`}
              >
                <FormattedMessage id="ChangePermissions" />
              </Button>
            ) : (
              ""
            )}
            <DisconnectTeacherFromClassButton
              promptRemoveTeacher={promptRemoveTeacher}
              teacher={teacher}
              removingTeacher={removingTeacher}
            />
          </div>
        ),
      teacherData: teacher,
    });
  });

  const searchList = () => {
    return classCoursesData.data.map((course, i) => (
      <Row key={i}>
        <Col>{course.name}</Col>
      </Row>
    ));
  };

  const toggleCoursesDropdown = () => {
    setShowCourses((prevstate) => !prevstate);
  };

  const programSelectOptions = [];
  props.teacherInfo.data.programmes.forEach((program) => {
    let prog = { value: program, label: program.name };
    programSelectOptions.push(prog);
  });

  const currentProgramOption = {
    value: props.classData.data.programme ? props.classData.data.programme : "",
    label: props.classData.data.programme
      ? props.classData.data.programme.name
      : "",
  };

  const orientationSelectOptions = [];
  props.schoolInfo.data.orientations.forEach((orientation) => {
    let orient = { value: orientation, label: orientation.name };
    orientationSelectOptions.push(orient);
  });

  const currentOrientation = { value: "", label: "" };
  if (props.classData.data.orientation != null) {
    currentOrientation.value = props.classData.data.orientation;
    currentOrientation.label = props.classData.data.orientation.name;
  }

  const getStudentIds = () => {
    let listOfIds = [];
    props.classData.data.students.forEach((stud) => {
      listOfIds.push(stud.id);
    });
    return listOfIds;
  };

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="ClassInformation" />
            {updatingClass ? (
              <div style={{ float: "right" }}>
                {" "}
                <div style={{ marginTop: "-11%", marginBottom: "22%" }}>
                  <PacmanLoader color={"#20a8d8"} size={15} loading={true} />
                </div>
                <FormattedMessage id="Updating" />
              </div>
            ) : (
              ""
            )}
            <hr />
          </CardTitle>

          <Row>
            <Col sm="6">
              <Form key={props.classData.data.id} action="" method="post">
                <Label htmlFor="className" className="form-label">
                  <FormattedMessage id="Class" />:
                </Label>
                <Input
                  type="text"
                  id="className"
                  name="className"
                  onBlur={handleChangedClassName}
                  defaultValue={props.classData.data.name}
                  placeholder={props.classData.data.name}
                  autoComplete="false"
                />
                {classNameInvalidSigns ? (
                  <span className="ms-2 text-danger">
                    <FontAwesomeIcon icon="exclamation" className="me-1" />
                    <FormattedMessage id="InvalidClassName" />
                  </span>
                ) : (
                  ""
                )}
                <Label htmlFor="classProgram" className="form-label">
                  <FormattedMessage id="Program" />:
                </Label>
                <Select
                  disabled={!props.fullAccessTeacher}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  name="classProgram"
                  options={programSelectOptions}
                  defaultValue={currentProgramOption}
                  onChange={(event) =>
                    handleChangeProgram(event, "classProgram")
                  }
                />
                <Label htmlFor="classProgram" className="form-label">
                  <FormattedMessage id="Orientation" />:
                </Label>
                <Select
                  disabled={!props.fullAccessTeacher}
                  menuPortalTarget={document.body}
                  name="classOrientation"
                  menuPosition={"fixed"}
                  options={orientationSelectOptions}
                  defaultValue={currentOrientation}
                  onChange={(event) =>
                    handleChangeProgram(event, "classOrientation")
                  }
                />
              </Form>
            </Col>
          </Row>
        </Card>
      </Col>

      <FocusControl
        targetSettings={fc.targetSettings}
        deviantStudents={fc.deviantStudents}
        onDelete={fc.handleDeleteFocusControl}
        deletingTarget={fc.deletingTarget}
        deviationSource="Class"
        inheritedSourceId="InheritedFromProgram"
        onSaveTarget={fc.handleSaveFocusTarget}
        savingTarget={fc.savingTarget}
      />

      <ModuleLocker
        courses={classCoursesData.data}
        level="Class"
        currentLevelId={props.classData.data.id}
        students={getStudentIds()}
      />

      <FinalExamUnlockerContainer
        programId={props.classData.data.programme.id}
        courses={classCoursesData.data}
        searchList={searchList}
        placeHolder={props.placeHolder}
        toggleCoursesDropdown={toggleCoursesDropdown}
        showCourses={showCourses}
        classId={props.classData.data.id}
        level="Class"
      />

      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="Students" />
            <hr className="pb-0 mb-0" />
          </CardTitle>
          <CardSubtitle>
            <div className="d-flex align-items-center flex-wrap m-1">
              <div className="m-2">
                <FontAwesomeIcon
                  icon="search"
                  className="position-absolute ms-3"
                  style={{ marginTop: "10px" }}
                />
                <Input
                  id="studentSearchInput"
                  className="rounded-4 ps-5"
                  onChange={(e) => setStudentFilterSearch(e.target.value)}
                  placeholder={intl.formatMessage({ id: "NameOrEmail" })}
                />
              </div>
              <Button
                size="sm"
                outline
                color="primary"
                className="my-1 m-y-md-0 me-md-1"
                onClick={() => openAddNewStudentToClassModal()}
              >
                <FormattedMessage id="AddNewStudentToClass" />
              </Button>
              <RequiresFullAccessHint hasFullAccess={props.fullAccessTeacher}>
                <Button
                  size="sm"
                  outline
                  color="primary"
                  disabled={!props.fullAccessTeacher}
                  className="my-1 m-y-md-0 mx-md-1"
                  onClick={() => openAddStudentToClassModal()}
                >
                  <FormattedMessage id="MoveExistingStudentsToClass" />
                </Button>
              </RequiresFullAccessHint>
              <RequiresFullAccessHint hasFullAccess={props.fullAccessTeacher}>
                <Button
                  size="sm"
                  outline
                  color="primary"
                  disabled={!props.fullAccessTeacher}
                  className="my-1 m-y-md-0 mx-md-1"
                  onClick={() => toggleImportStudentsModal()}
                >
                  <FormattedMessage id="ImportFromXlsx" />
                </Button>
              </RequiresFullAccessHint>
            </div>
          </CardSubtitle>

          <DataGrid
            columns={studentTableColumns}
            rows={studentTableDataSource}
          />
        </Card>
      </Col>

      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="Teachers" />
            <hr className="pb-0 mb-0" />
          </CardTitle>
          <CardSubtitle>
            <div className="d-flex align-items-center flex-wrap m-1">
              <div className="m-2">
                <FontAwesomeIcon
                  icon="search"
                  className="position-absolute ms-3"
                  style={{ marginTop: "10px" }}
                />
                <Input
                  id="teacherSearchInput"
                  className="rounded-4 ps-5"
                  onChange={(e) => setTeacherFilterSearch(e.target.value)}
                  placeholder={intl.formatMessage({ id: "NameOrEmail" })}
                />
              </div>
              <Button
                size="sm"
                outline
                color="primary"
                className="my-1 m-y-md-0 me-md-1"
                onClick={toggleNewTeacherModal}
              >
                <FormattedMessage id="AddNewTeacher" />
              </Button>
              <Button
                size="sm"
                outline
                color="primary"
                className="my-1 m-y-md-0 mx-md-1"
                onClick={toggleExistingTeachersModal}
              >
                <FormattedMessage id="AddExistingTeachersToClass" />
              </Button>
            </div>
          </CardSubtitle>
          <DataGrid
            columns={teacherTableColumns}
            rows={teacherTableDataSource}
          />
        </Card>
      </Col>

      <AddNewTeacherToClass
        isOpen={addNewTeacherModalOpen}
        toggle={toggleNewTeacherModal}
        schoolClass={props.classData.data}
        onSubmit={handleAddNewTeacher}
        fullAccessTeacher={props.fullAccessTeacher}
        purchaserTeacher={props.teacherInfo.data.purchaser}
      />
      <AddTeachersToClass
        classId={props.classData.data.id}
        isOpen={addExistingTeachersModalOpen}
        toggle={toggleExistingTeachersModal}
        teachers={props.schoolInfo.data.teachers}
        existingTeachers={props.classData.data.teachers}
        loadTeachers={reloadClass}
      />
      <ConfirmDialog
        isOpen={promptTeacherRemoval}
        toggle={() => setPromptTeacherRemoval(!promptTeacherRemoval)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ teacherName: teacherForRemoval.name }}
            id="WillBeRemovedFromClass"
          />
        }
        onProceed={handleRemoveTeacher}
        danger={true}
      />
      <ConfirmDialog
        isOpen={promptStudentRemoval}
        toggle={() => setPromptStudentRemoval(!promptStudentRemoval)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{ studentName: studentForRemoval.name }}
            id="StudentWillBeDeleted"
          />
        }
        onProceed={handleDeleteStudent}
        danger={true}
      />
      <StudentLicenseManagemenModal
        isOpen={licenseModalOpen}
        toggle={toggleStudentLicenseModal}
        preselectedStudents={studentsForLicenseModal}
        className={props.classData.data.name}
      />
      <CreateStudentInClass
        schoolClass={props.classData.data}
        addNewStudentToClassModal={addNewStudentToClassModal}
        toggle={() => toggleModals("addNewStudentToClass")}
        purchaserTeacher={props.teacherInfo.data.purchaser}
      />

      <MoveExistingStudentToClassModal
        classId={props.classData.data.id}
        isOpen={addStudentToClassModal}
        toggle={() => toggleModals("addStudentToClass")}
        students={allStudents}
        loadStudents={reloadClass}
        existingStudents={studentsInClass}
      />

      <ImportStudentsToClassModal
        preSelectedClass={true}
        preSelectedClassId={props.classData.data.id}
        reloadClass={reloadClass}
        handleHide={toggleImportStudentsModal}
        studentClasses={[]}
        isOpen={importStudentsModalOpen}
      />
    </div>
  );
}
